import { Drawer, Group, Stack, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import { Button, SearchInput } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useState, useMemo } from "react"

import { productTypeUpdateSchema } from "@tfg/types/schemas/admin"

import { HeaderControlsProvider } from "~/components/contexts/useHeaderControlsContext"
import {
  FindProductTypesQueryResponse,
  FindProductTypesRequestSortsDto,
} from "~/gen/api"
import { useFindProductTypes } from "~/gen/api/hooks/useFindProductTypes"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { useFindOperationChainsQuery } from "~/pages/Admin/Ingestion/OpChains/hooks/useFindOperationChainsQuery"
import { ProductTypesSort } from "~/pages/Admin/MasterLists/ProductTypes/ProductTypesSort"
import { ProductTypesTable } from "~/pages/Admin/MasterLists/ProductTypes/ProductTypesTable"
import { useCreateProductTypeMutation } from "~/pages/Admin/MasterLists/ProductTypes/hooks"
import { TypesForm } from "~/pages/Admin/components/TypesForm"
import { FormFieldConfig } from "~/pages/Admin/components/form.types"

const ProductTypesAdminWrapperContent = () => {
  const [search, setSearch] = useState("")
  const [sorts, setSorts] = useState<FindProductTypesRequestSortsDto>({})
  const { data, totalCount, setPageBasedPagination, pageBasedPagination } =
    usePaginatedQuery<
      typeof useFindProductTypes,
      FindProductTypesQueryResponse
    >(useFindProductTypes, {
      search,
      sorts,
    })
  const { data: productOpChains, isLoading: productOpChainsLoading } =
    useFindOperationChainsQuery({
      type: "PRODUCT",
      showDeleted: false,
    })

  const [drawerOpen, drawerActions] = useDisclosure(false)
  const { mutate: createProductType } = useCreateProductTypeMutation({
    onSuccess: drawerActions.close,
  })

  // Create form configuration reactively based on loaded data
  const formConfig = useMemo(
    () => ({
      validate: zod4Resolver(productTypeUpdateSchema),
      initialValues: {
        name: "",
        ecobalyseId: "",
        operationChainId: productOpChains?.data?.[0]?.id ?? "",
      },
    }),
    [productOpChains?.data]
  )

  const fields: FormFieldConfig[] = useMemo(
    () => [
      {
        type: "text",
        name: "name",
        label: "Key",
        placeholder: "Product Type Key",
      },
      {
        type: "select",
        name: "operationChainId",
        label: "Operation Chain",
        placeholder: "Product Type Operation Chain",
        options:
          productOpChains?.data?.map((opChain) => ({
            label: opChain.name,
            value: opChain.id,
          })) ?? [],
      },
      {
        type: "text",
        name: "ecobalyseId",
        label: "Ecobalyse Id",
        placeholder: "Ecobalyse Id",
      },
    ],
    [productOpChains?.data]
  )

  // Check if required data is loaded
  const isDataLoaded = !productOpChainsLoading && productOpChains?.data

  return (
    <Stack>
      <Group justify="space-between">
        <Group>
          <SearchInput
            inputValue={search}
            onChange={(value) => setSearch(value.toString())}
            placeholder="Search Product Types"
          />
          <ProductTypesSort sorts={sorts} setSorts={setSorts} />
        </Group>
        <Button onClick={drawerActions.open} icon={<IconPlus />}>
          New Product Type
        </Button>
      </Group>
      <Drawer
        position="right"
        opened={drawerOpen}
        onClose={() => drawerActions.close()}
        title={<Title order={2}>Create Product Type</Title>}
        size="lg"
      >
        {isDataLoaded ? (
          <TypesForm
            elements={fields}
            submitLabel="Create"
            formConfig={formConfig}
            mutate={createProductType}
          />
        ) : (
          <div>Loading form data...</div>
        )}
      </Drawer>
      {data?.data && (
        <ProductTypesTable
          productTypes={data.data}
          total={totalCount}
          pagination={pageBasedPagination}
          setPagination={setPageBasedPagination}
        />
      )}
    </Stack>
  )
}

export const ProductTypesAdminWrapper = () => {
  return (
    <HeaderControlsProvider>
      <ProductTypesAdminWrapperContent />
    </HeaderControlsProvider>
  )
}
